import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CryptoService {

  private async deriveKey(passphrase: string, saltArrayBuffer: ArrayBuffer): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const passKey = await crypto.subtle.importKey(
      'raw',
      enc.encode(passphrase),
      'PBKDF2',
      false,
      ['deriveKey']
    );

    return crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: saltArrayBuffer,
        iterations: 100_000,
        hash: 'SHA-256'
      },
      passKey,
      { name: 'AES-GCM', length: 256 },
      false,
      ['decrypt']
    );
  }

  // Decrypt the Base64 blob produced by the C# tool (salt[16] || iv[12] || cipher || tag[16])
  async decryptBase64(encryptedBase64: string, passphrase: string): Promise<string> {
    const raw = this.base64ToUint8Array(encryptedBase64);

    const salt = raw.slice(0, 16);
    const iv = raw.slice(16, 28); // 12 bytes
    const cipherAndTag = raw.slice(28); // cipher || tag

    // Create an ArrayBuffer that exactly represents the salt bytes (avoids SharedArrayBuffer typing issues)
    const saltArrayBuffer = salt.buffer.slice(salt.byteOffset, salt.byteOffset + salt.byteLength);

    const key = await this.deriveKey(passphrase, saltArrayBuffer);

    const decrypted = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      cipherAndTag
    );

    const dec = new TextDecoder();
    return dec.decode(decrypted);
  }

  private base64ToUint8Array(base64: string): Uint8Array {
    const binary = atob(base64);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }
}